import { useEffect, useState } from 'react';
import { getCriticalAlerts, getPendingIssues } from '../services';
import { ICriticalAlertItem, IPendingIssueItem, HookState } from '../types';
import { resolveErrorMessage } from './hookUtils';

export interface AlertsState {
	criticalAlerts: HookState<ICriticalAlertItem[]>;
	pendingIssues: HookState<IPendingIssueItem[]>;
}

export function useAlerts(options?: { sellerId?: number; team?: string; enabled?: boolean }): AlertsState {
	const [criticalAlerts, setCriticalAlerts] = useState<HookState<ICriticalAlertItem[]>>({
		data: null,
		loading: true,
		error: null,
		cachedAt: null,
	});

	const [pendingIssues, setPendingIssues] = useState<HookState<IPendingIssueItem[]>>({
		data: null,
		loading: true,
		error: null,
		cachedAt: null,
	});

	const sellerId = options?.sellerId;
	const team = options?.team;
	const enabled = options?.enabled ?? true;

	useEffect(() => {
		if (!enabled) return;

		let cancelled = false;
		const controller = new AbortController();

		setCriticalAlerts((prev) => ({ ...prev, loading: true, error: null }));
		getCriticalAlerts({ sellerId, team })
			.then(({ data, cachedAt }) => {
				if (!cancelled) setCriticalAlerts({ data, loading: false, error: null, cachedAt });
			})
			.catch((err) => {
				if (!cancelled && !controller.signal.aborted)
					setCriticalAlerts({ data: null, loading: false, error: resolveErrorMessage(err), cachedAt: null });
			});

		setPendingIssues((prev) => ({ ...prev, loading: true, error: null }));
		getPendingIssues({ sellerId, team })
			.then(({ data, cachedAt }) => {
				if (!cancelled) setPendingIssues({ data, loading: false, error: null, cachedAt });
			})
			.catch((err) => {
				if (!cancelled && !controller.signal.aborted)
					setPendingIssues({ data: null, loading: false, error: resolveErrorMessage(err), cachedAt: null });
			});

		return () => {
			cancelled = true;
			controller.abort();
		};
	}, [sellerId, team, enabled]);

	return { criticalAlerts, pendingIssues };
}
